import React, { useState, useEffect, useRef, Component } from 'react';
import { API, COLORS, ROUTINE_CYCLE } from './utils/constants';
import TabBar from './components/TabBar';
import HomeScreen from './screens/HomeScreen';
import WorkoutScreen from './screens/WorkoutScreen';
import RecipesScreen from './screens/RecipesScreen';
import BaseDatosScreen from './screens/BaseDatosScreen';
import LoginScreen from './screens/LoginScreen';
import SettingsScreen, { DEFAULT_SETTINGS } from './screens/SettingsScreen';
import ChatScreen from './screens/ChatScreen';

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) return (
      <div style={{ background: '#0a0a0f', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 30, fontFamily: 'monospace' }}>
        <div style={{ fontSize: 32, marginBottom: 16 }}>⚠</div>
        <div style={{ fontSize: 13, maxWidth: 340, wordBreak: 'break-all', textAlign: 'center', color: '#f0f0f8' }}>{String(this.state.error)}</div>
        <button onClick={() => window.location.reload()} style={{ marginTop: 24, padding: '10px 24px', background: '#ff6b35', border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', fontWeight: 700 }}>Recargar</button>
      </div>
    );
    return this.props.children;
  }
}

const TEMA_BG = {
  dark:   '#0a0a0f',
  amoled: '#000000',
};

const AURORA_TINTS = {
  purple: 'linear-gradient(135deg, #2D1A4A 0%, #0F2D4A 50%, #0a0a0f 100%)',
  green:  'linear-gradient(135deg, #1A3D15 0%, #0D2510 50%, #0a0a0f 100%)',
  orange: 'linear-gradient(135deg, #3D2008 0%, #251408 50%, #0a0a0f 100%)',
  blue:   'linear-gradient(135deg, #0A2040 0%, #081828 50%, #0a0a0f 100%)',
  red:    'linear-gradient(135deg, #3D0A0A 0%, #250808 50%, #0a0a0f 100%)',
  cyan:   'linear-gradient(135deg, #0A3D3D 0%, #082525 50%, #0a0a0f 100%)',
  gold:   'linear-gradient(135deg, #3D2E00 0%, #251E00 50%, #0a0a0f 100%)',
  pink:   'linear-gradient(135deg, #3D0A20 0%, #250810 50%, #0a0a0f 100%)',
};

const SWIPE_TABS = ["home", "gym", "recipes", "base", "settings"];

function App() {
  const [tab, setTab] = useState("home");
  const touchStartRef = useRef(null);
  const [coachPrompt, setCoachPrompt] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState({ username: "", nombre: "", handle: "" });
  const [isDemo, setIsDemo] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Settings viven aquí — la BD es la fuente de verdad, nunca localStorage
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  // Rutina activa — cargada desde BD
  const [activeRoutine, setActiveRoutine] = useState(null);

  const [nutrition, setNutrition] = useState({
    calories: { goal: DEFAULT_SETTINGS.goal_kcal, current: 0 },
    foods: [],
    protein: { goal: DEFAULT_SETTINGS.goal_proteina, current: 0 },
    carbs:   { goal: DEFAULT_SETTINGS.goal_carbos,  current: 0 },
    fat:     { goal: DEFAULT_SETTINGS.goal_grasa,   current: 0 },
  });

  const [todayType, setTodayType] = useState('Pull'); // se sobreescribe al cargar el ciclo
  const [cicloNextDay, setCicloNextDay] = useState('Pull'); // día pendiente real del ciclo

  const handleSetTodayType = (type) => {
    // Override manual solo afecta al display, no al ciclo
    setTodayType(type);
  };

  const loadCiclo = async () => {
    try {
      const r = await fetch(API + '/entrenamiento/ciclo', { credentials: 'include' });
      const data = await r.json();
      if (data?.next_day) {
        setCicloNextDay(data.next_day);
        localStorage.removeItem('fitapp_day_override');
        // Si se guardó sesión hoy y aún no son las 00:00, mantener ese día visible
        const today = new Date().toISOString().split('T')[0];
        const hold = (() => { try { return JSON.parse(localStorage.getItem('fitapp_day_hold')); } catch { return null; } })();
        if (hold?.date === today) {
          setTodayType(hold.savedType);
        } else {
          localStorage.removeItem('fitapp_day_hold');
          setTodayType(data.next_day);
        }
      }
    } catch {}
  };

  // BUILD_ID: fuerza recarga cuando hay nuevo bundle (bypasea caché iOS PWA)
  useEffect(() => {
    const BUILD_ID = process.env.REACT_APP_BUILD_ID || '0';
    if (BUILD_ID === '0') return;
    const stored = localStorage.getItem('fitapp_build_id');
    if (stored && stored !== BUILD_ID) {
      localStorage.setItem('fitapp_build_id', BUILD_ID);
      window.location.replace(window.location.href.split('?')[0] + '?v=' + BUILD_ID);
    } else {
      localStorage.setItem('fitapp_build_id', BUILD_ID);
    }
  }, []);

  // Cuando cambia la rutina activa, validar que todayType esté en el ciclo
  useEffect(() => {
    if (!activeRoutine?.ciclo) return;
    if (!activeRoutine.ciclo.includes(todayType)) {
      handleSetTodayType(activeRoutine.ciclo[0]);
    }
  }, [activeRoutine]);

  // Cambiar al siguiente día del ciclo a las 00:00
  useEffect(() => {
    if (!cicloNextDay || cicloNextDay === todayType) return;
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const ms = midnight - now;
    const t = setTimeout(() => {
      localStorage.removeItem('fitapp_day_hold');
      setTodayType(cicloNextDay);
    }, ms);
    return () => clearTimeout(t);
  }, [cicloNextDay, todayType]);

  // Helper para cargar la rutina activa
  const loadActiveRoutine = async () => {
    try {
      const r = await fetch(API + '/rutinas/activa', { credentials: 'include' });
      const data = await r.json();
      if (data && data.id) setActiveRoutine(data);
    } catch {}
  };

  // Cuando settings cambian, aplicar efectos de sistema
  useEffect(() => {
    if (settings.bloquear_rotacion && window.screen.orientation?.lock) {
      window.screen.orientation.lock('portrait').catch(() => {});
    }
    if (settings.shake_undo) {
      window._shakeHandler = e => e.preventDefault();
      window.addEventListener('devicemotion', window._shakeHandler, { passive: false });
    } else if (window._shakeHandler) {
      window.removeEventListener('devicemotion', window._shakeHandler);
      window._shakeHandler = null;
    }
    return () => {
      if (window._shakeHandler) window.removeEventListener('devicemotion', window._shakeHandler);
    };
  }, [settings.bloquear_rotacion, settings.shake_undo]);

  // Auth check + carga de settings + rutina activa
  useEffect(() => {
    fetch(API + "/auth/check", { credentials: "include" })
      .then(r => r.json())
      .then(async data => {
        if (data.ok) {
          setCurrentUser({ username: data.username, nombre: data.nombre, handle: data.handle || data.username });
          setLoggedIn(true);
          try {
            const sr = await fetch(API + "/settings", { credentials: "include" });
            const srv = await sr.json();
            const loaded = (srv && typeof srv === "object" && !srv.detail && Object.keys(srv).length > 0)
              ? { ...DEFAULT_SETTINGS, ...srv }
              : { ...DEFAULT_SETTINGS };
            setSettings(loaded);
          } catch {
            setSettings({ ...DEFAULT_SETTINGS });
          }
          await loadActiveRoutine();
          await loadCiclo();
        }
        setCheckingAuth(false);
      })
      .catch(() => setCheckingAuth(false));
  }, []);

  // Actualizar goals de nutrición cuando cambian los settings
  useEffect(() => {
    setNutrition(prev => ({
      ...prev,
      calories: { ...prev.calories, goal: settings.goal_kcal },
      protein:  { ...prev.protein,  goal: settings.goal_proteina },
      carbs:    { ...prev.carbs,    goal: settings.goal_carbos },
      fat:      { ...prev.fat,      goal: settings.goal_grasa },
    }));
  }, [settings.goal_kcal, settings.goal_proteina, settings.goal_carbos, settings.goal_grasa]);

  // Cargar datos de nutrición del día al hacer login
  useEffect(() => {
    if (!loggedIn) return;
    const today = new Date().toISOString().split("T")[0];
    Promise.all([
      fetch(API + `/nutricion/${today}`, { credentials: "include" }).then(r => r.json()).catch(() => null),
      fetch(API + `/food-log/${today}`, { credentials: "include" }).then(r => r.json()).catch(() => []),
    ]).then(([data, logData]) => {
      const foods = (logData||[]).map(f => ({ id: f.id, name: f.nombre, calories: f.calorias, protein: f.proteina, carbos: f.carbos, fat: f.grasa, grams: f.gramos, time: f.hora, tipo_comida: f.tipo_comida, tipo_medida_usada: f.tipo_medida_usada, cantidad_usada: f.cantidad_usada, peso_unidad: f.peso_unidad }));
      if (data || foods.length > 0) {
        setNutrition(prev => ({
          ...prev,
          calories: { ...prev.calories, current: foods.reduce((acc, f) => acc + (Number(f.calories) || 0), 0) },
          protein:  { ...prev.protein,  current: foods.reduce((acc, f) => acc + (Number(f.protein)  || 0), 0) },
          carbs:    { ...prev.carbs,    current: foods.reduce((acc, f) => acc + (Number(f.carbos)   || 0), 0) },
          fat:      { ...prev.fat,      current: foods.reduce((acc, f) => acc + (Number(f.fat)      || 0), 0) },
          foods,
        }));
      }
    }).catch(() => {});
  }, [loggedIn]);

  // Cargar settings del servidor y hacer login
  const handleLogin = async (user) => {
    setIsDemo(!!user.demo);
    setCurrentUser({ ...user, handle: user.handle || user.username });
    try {
      const sr = await fetch(API + '/settings', { credentials: 'include' });
      const srv = await sr.json();
      const loaded = (srv && typeof srv === 'object' && !srv.detail && Object.keys(srv).length > 0)
        ? { ...DEFAULT_SETTINGS, ...srv }
        : { ...DEFAULT_SETTINGS };
      setSettings(loaded);
    } catch {
      setSettings({ ...DEFAULT_SETTINGS });
    }
    await loadActiveRoutine();
    await loadCiclo();
    setLoggedIn(true);
    const urlTab = new URLSearchParams(window.location.search).get('tab');
    if (urlTab) { setTab(urlTab); window.history.replaceState({}, '', window.location.pathname); }
  };

  // Guardar settings en el servidor y actualizar estado local
  const handleHandleChange = (newHandle) => {
    setCurrentUser(prev => ({ ...prev, handle: newHandle }));
  };

  const handleSettingsChange = (newSettings) => {
    setSettings(newSettings);
    if (newSettings.nombre !== undefined) {
      setCurrentUser(prev => ({ ...prev, nombre: newSettings.nombre }));
    }
    fetch(API + '/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(newSettings),
    }).catch(() => {});
  };

  // SWIPE DESACTIVADO TEMPORALMENTE
  const handleTouchStart = (_e) => {};
  const handleTouchEnd   = (_e) => {};

  const resetToken = new URLSearchParams(window.location.search).get("reset_token");
  if (resetToken) return <LoginScreen onLogin={handleLogin} />;

  if (checkingAuth) return <div style={{ background: "#0a0a0f", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#8888a8", fontSize: 32 }}>◈</span></div>;
  if (!loggedIn) return <LoginScreen onLogin={handleLogin} />;

  const appBg = settings.tema === 'aurora'
    ? (AURORA_TINTS[settings.bg_tint] || AURORA_TINTS.purple)
    : (TEMA_BG[settings.tema] || '#0a0a0f');

  return (
    <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} style={{ background: appBg, minHeight: "100vh", color: COLORS.text, paddingTop: isDemo ? 30 : 0, fontFamily: "'Helvetica Neue', Arial, sans-serif", maxWidth: 430, margin: "0 auto", position: "relative" }}>
      {isDemo && (
        <div style={{ position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 430, zIndex: 9998, background: "linear-gradient(90deg, #f59e0b, #ef4444)", padding: "6px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", boxSizing: "border-box" }}>
          <span style={{ color: "#fff", fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>🎭 MODO DEMO — Los cambios no se guardan</span>
          <button onClick={async () => {
            await fetch(API + "/auth/logout", { method: "POST", credentials: "include" }).catch(() => {});
            const base = window.location.href.split('?')[0];
            window.location.replace(base + '?r=' + Date.now());
          }} style={{ background: "rgba(255,255,255,0.25)", border: "none", borderRadius: 6, padding: "3px 8px", color: "#fff", fontSize: 10, fontWeight: 800, cursor: "pointer" }}>SALIR</button>
        </div>
      )}
      {!isDemo && currentUser.username === 'carlos' && <button onClick={async () => {
        await fetch(API + "/auth/logout", { method: "POST", credentials: "include" }).catch(() => {});
        if ("caches" in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map(k => caches.delete(k)));
        }
        const base = window.location.href.split('?')[0];
        window.location.replace(base + '?r=' + Date.now());
      }} style={{ position: "fixed", top: 12, right: 12, zIndex: 9999, background: "#1a1a26cc", border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "6px 10px", color: COLORS.textMuted, fontSize: 11, cursor: "pointer", backdropFilter: "blur(8px)" }}>↺ Reset</button>}

      {tab === "home" && <HomeScreen todayType={todayType} setTodayType={handleSetTodayType} nutrition={nutrition} setNutrition={setNutrition} setTab={setTab} currentUser={currentUser} />}
      <div style={{ display: tab === "gym" ? "block" : "none" }}>
        <WorkoutScreen
          todayType={todayType}
          setTodayType={handleSetTodayType}
          setTab={setTab}
          settings={settings}
          activeRoutine={activeRoutine}
          setActiveRoutine={setActiveRoutine}
          onOpenCoach={(prompt) => { setCoachPrompt(prompt || null); setTab('coach'); }}
          cicloNextDay={cicloNextDay}
          onCicloAvanzar={async (tipoDia, fecha) => {
            try {
              const r = await fetch(API + '/entrenamiento/ciclo/avanzar', {
                method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({ tipo_dia: tipoDia, fecha }),
              });
              const data = await r.json();
              if (data.ok) {
                setCicloNextDay(data.next_day);
                // Guardar hold para que un reload antes de medianoche no cambie el día
                localStorage.setItem('fitapp_day_hold', JSON.stringify({ date: fecha, savedType: tipoDia }));
              }
            } catch {}
          }}
        />
      </div>
      {tab === "recipes" && <RecipesScreen />}
      {tab === "base" && <BaseDatosScreen />}
      {tab === "settings" && <SettingsScreen settings={settings} onSettingsChange={handleSettingsChange} currentUser={currentUser} onHandleChange={handleHandleChange} />}
      {tab === "coach" && <ChatScreen currentUser={currentUser} onBack={() => setTab("gym")} initialPrompt={coachPrompt} onPromptConsumed={() => setCoachPrompt(null)} />}
      {tab !== "coach" && <TabBar active={tab} setActive={setTab} />}
    </div>
  );
}

const AppWithBoundary = () => <ErrorBoundary><App /></ErrorBoundary>;
export default AppWithBoundary;
